import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { centerCropToOutput, PHOTO_ASPECT_H, PHOTO_ASPECT_W } from '../lib/photo';
import './CameraCapture.scss';


const COUNTDOWN_SECONDS = 3;

export default function CameraCapture({
  onCapture,
  onError,
  onShutter,
  onBack,
}: {
  onCapture: (dataUrl: string) => void;
  onError: (kind: 'denied' | 'unavailable') => void;
  onShutter?: () => void;
  onBack?: () => void;
}) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const captureTimerRef = useRef<number | null>(null);
  // Callback props go through refs so the camera setup effect can stay
  // mounted-once. If we put `onError`/`onCapture`/`onShutter` in deps and the
  // parent passed them as inline arrows (PhotoRight does), every parent
  // re-render — including the one triggered by setFlashing(true) — would tear
  // down the stream and re-call getUserMedia. The capture fired 60ms later
  // would then read a black frame (Chrome) or videoWidth=0 (Safari).
  const onCaptureRef = useRef(onCapture);
  const onErrorRef = useRef(onError);
  const onShutterRef = useRef(onShutter);
  useEffect(() => {
    onCaptureRef.current = onCapture;
    onErrorRef.current = onError;
    onShutterRef.current = onShutter;
  });
  const [countdown, setCountdown] = useState<number | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function start() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 1536 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((tr) => tr.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
          setReady(true);
        }
      } catch (err) {
        const name = (err as DOMException | undefined)?.name;
        if (name === 'NotAllowedError' || name === 'SecurityError') {
          onErrorRef.current('denied');
        } else {
          onErrorRef.current('unavailable');
        }
      }
    }
    void start();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    };
  }, []);

  const capture = useCallback(() => {
    const video = videoRef.current;
    if (!video || video.videoWidth === 0) return;

    const off = document.createElement('canvas');
    off.width = video.videoWidth;
    off.height = video.videoHeight;
    const ctx = off.getContext('2d');
    if (!ctx) return;

    // Mirror flip so what the user "saw" matches what's saved.
    ctx.translate(off.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0);

    const dataUrl = centerCropToOutput(off);
    onCaptureRef.current(dataUrl);
  }, []);

  useEffect(() => {
    if (countdown === null) return undefined;
    const id = window.setTimeout(() => {
      if (countdown === 1) {
        // Fire the flash first so it paints and illuminates the subject for a
        // couple of frames before the actual frame is grabbed.
        onShutterRef.current?.();
        captureTimerRef.current = window.setTimeout(() => {
          capture();
          captureTimerRef.current = null;
        }, 60);
        setCountdown(null);
      } else {
        setCountdown(countdown - 1);
      }
    }, 1000);
    return () => window.clearTimeout(id);
  }, [countdown, capture]);

  useEffect(() => {
    return () => {
      if (captureTimerRef.current !== null) window.clearTimeout(captureTimerRef.current);
    };
  }, []);

  return (
    <div className="camera">
      <div
        className="camera__viewport"
        style={{ aspectRatio: `${PHOTO_ASPECT_W} / ${PHOTO_ASPECT_H}` }}
      >
        <video ref={videoRef} className="camera__video" muted playsInline />
        <div className="camera__guide" aria-hidden="true" />
        {!ready && (
          <div className="camera__loader" aria-hidden="true">
            <span className="camera__loader-ring" />
          </div>
        )}
        {countdown !== null && countdown > 0 && (
          // `key` remounts the element on each tick so the CSS animation
          // restarts — without it the same node would persist 3 → 2 → 1 and
          // the animation would only play once.
          <div key={countdown} className="camera__countdown" aria-live="polite">
            {countdown}
          </div>
        )}
      </div>

      <div className="camera__controls">
        {onBack && (
          <button
            type="button"
            className="camera__back"
            onClick={onBack}
            disabled={countdown !== null}
            aria-label={t('photoIntro.back')}
          >
            <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
              <path
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 6l-6 6 6 6"
              />
            </svg>
          </button>
        )}
        <button
          type="button"
          className="camera__shutter"
          disabled={!ready || countdown !== null}
          onClick={() => setCountdown(COUNTDOWN_SECONDS)}
          aria-label={t('photo.shutter')}
        >
          <span className="camera__shutter-ring" />
        </button>
      </div>
    </div>
  );
}
